import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignPersonnel,
  createPersonnel,
  createAdminProjectVehicle,
  createProject,
  fetchAdminAuditLogs,
  fetchAdminLogs,
  fetchAdminPersonnel,
  fetchAdminProjectVehicles,
  fetchProjects,
  removePersonnel,
  updateAdminProjectVehicle,
  updateAdminProjectVehicleSettings,
  updatePersonnel,
} from "../api";
import { getApiErrorMessage } from "../api/client";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import "./AdminPanel.css";

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const modalCardStyle = {
  background: "white",
  borderRadius: "12px",
  padding: "24px",
  width: "500px",
  maxWidth: "90%",
  maxHeight: "80vh",
  overflowY: "auto",
  boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
};
const buttonStyle = {
  padding: "6px 12px",
  borderRadius: "6px",
  fontSize: "13px",
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
};

const ROLE_MATRIX_ROWS = [
  ["Proje oluştur/sil", "Evet", "Hayır", "Hayır"],
  ["Pafta silme", "Evet", "Evet (kendi projesi)", "Hayır"],
  ["Takım ekle/sil", "Evet", "Evet (kendi projesi)", "Hayır"],
  ["Takım üyesi düzenleme", "Evet", "Evet (kendi projesi)", "Hayır"],
  ["Personel ekle", "Evet", "Evet (kendi projesi)", "Hayır"],
  ["Şifre sıfırlama", "Evet", "Evet (kendi projesi, çift onay)", "Hayır"],
  ["Rol atama/değiştirme", "Evet", "Hayır", "Hayır"],
  ["Geçici yönetici atama", "Evet", "Hayır", "Hayır"],
  ["Araç havuzu yönetimi", "Evet", "Ayara bağlı (kendi projesi)", "Hayır"],
  ["Kullanıcı pasife alma", "Evet (çift onay)", "Hayır", "Hayır"],
  ["Denetim kayıtları", "Tüm projeler", "Sadece kendi projesi", "Hayır"],
];

function toInputDate(timestampMs) {
  if (!timestampMs) return "";
  const date = new Date(timestampMs);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function roleLabel(role) {
  if (role === "project_admin") return "Yönetici";
  if (role === "superadmin") return "Süper Admin";
  return "Kullanıcı";
}

function normalizePlate(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleUpperCase("tr-TR");
}

function normalizeVehicleManagerRole(value) {
  return value === "superadmin" ? "superadmin" : "project_admin";
}

function resolveVehicleScopeProjectId(projects, preferredProjectId, fallbackProjectId) {
  const projectList = Array.isArray(projects) ? projects : [];
  if (projectList.length === 0) return null;

  const projectIds = new Set(projectList.map((project) => String(project.id)));
  const candidates = [preferredProjectId, fallbackProjectId];

  for (const candidate of candidates) {
    const normalizedCandidate = String(candidate || "").trim();
    if (normalizedCandidate && projectIds.has(normalizedCandidate)) {
      return normalizedCandidate;
    }
  }

  return String(projectList[0].id);
}

function getPersonnelAdminInfo(person, scopedProjectId) {
  if (person?.isSuperadmin || person?.projectRoles?.ALL === "superadmin") {
    return {
      adminTitle: "Admin",
      duty: "Sistem genel yönetimi, proje/yetki/personel yönetimi",
      badgeClass: "admin",
    };
  }

  const projectRoles = person?.projectRoles || {};
  const scopedRole = scopedProjectId ? projectRoles?.[scopedProjectId] : null;

  if (scopedRole === "project_admin") {
    return {
      adminTitle: "2. Admin",
      duty: "Bu projede pafta, ekip ve personel yönetimi",
      badgeClass: "admin",
    };
  }

  const hasProjectAdminRole = Object.values(projectRoles).some(
    (role) => role === "project_admin"
  );

  if (!scopedProjectId && hasProjectAdminRole) {
    return {
      adminTitle: "2. Admin",
      duty: "Atandığı projelerde pafta, ekip ve personel yönetimi",
      badgeClass: "admin",
    };
  }

  return {
    adminTitle: "Personel",
    duty: "Saha işlemleri, veri girişi ve görüntüleme",
    badgeClass: "user",
  };
}

function getAdminApiErrorMessage(error, fallback) {
  const responseBody =
    typeof error?.response?.data === "string" ? error.response.data : "";

  if (
    responseBody.includes("Could not proxy request") ||
    /Network Error/i.test(String(error?.message || "")) ||
    Number(error?.response?.status) === 500
  ) {
    return "API sunucusuna bağlanılamadı. Admin paneli için backend'in 4000 portunda açık olması gerekir.";
  }

  return getApiErrorMessage(error, fallback);
}

export default function AdminPanel({ user, selectedProjectId, onLogout, onBackToMap }) {
  const isOnline = useOnlineStatus();
  const [activeTab, setActiveTab] = useState(user.role === "superadmin" ? "projects" : "personnel");
  const [projects, setProjects] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [logs, setLogs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showProjectForm, setShowProjectForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [showPersonnelForm, setShowPersonnelForm] = useState(false);
  const [newPersonnelName, setNewPersonnelName] = useState("");
  const [newPersonnelEmail, setNewPersonnelEmail] = useState("");
  const [newPersonnelUsername, setNewPersonnelUsername] = useState("");
  const [newPersonnelPassword, setNewPersonnelPassword] = useState("");
  const [newPersonnelRole, setNewPersonnelRole] = useState("user");

  const [assignUser, setAssignUser] = useState(null);
  const [assignSelections, setAssignSelections] = useState({});
  const [editUser, setEditUser] = useState(null);
  const [vehicleFleet, setVehicleFleet] = useState([]);
  const [vehicleProjectId, setVehicleProjectId] = useState(null);
  const [newVehiclePlate, setNewVehiclePlate] = useState("");
  const [vehicleEditForm, setVehicleEditForm] = useState(null);
  const [vehicleManagerRole, setVehicleManagerRole] = useState("project_admin");
  const [logStartDate, setLogStartDate] = useState("");
  const [logEndDate, setLogEndDate] = useState("");

  const scopedProjectId = user.role === "superadmin" ? null : selectedProjectId;
  const scopeTitle = useMemo(
    () => (user.role === "superadmin" ? "Tüm Projeler" : `Proje: ${selectedProjectId || "-"}`),
    [selectedProjectId, user.role]
  );
  const isSuperAdmin =
    user.role === "superadmin" || user.projectRoles?.ALL === "superadmin";
  // Araç havuzu proje bazlıdır; süper admin bu ekranda hedef projeyi seçer.
  const vehicleScopeProjectId = useMemo(
    () =>
      user.role === "superadmin"
        ? resolveVehicleScopeProjectId(projects, vehicleProjectId, selectedProjectId)
        : selectedProjectId,
    [projects, selectedProjectId, user.role, vehicleProjectId]
  );
  const vehicleScopeProject = useMemo(
    () =>
      projects.find((project) => String(project.id) === String(vehicleScopeProjectId)) ||
      null,
    [projects, vehicleScopeProjectId]
  );
  const vehicleScopeProjectName =
    vehicleScopeProject?.name || vehicleScopeProjectId || "-";
  const canEditVehiclePermission = isSuperAdmin;
  const isVehicleScopedProjectAdmin = Boolean(
    vehicleScopeProjectId &&
      (user.projectRoles?.[vehicleScopeProjectId] === "project_admin" ||
        (user.role === "project_admin" &&
          String(user.projectId || "") === String(vehicleScopeProjectId)))
  );
  const canManageVehicleFleet =
    isSuperAdmin ||
    (vehicleManagerRole === "project_admin" && isVehicleScopedProjectAdmin);

  const ensureOnline = useCallback(
    (actionLabel) => {
      if (isOnline) return true;
      alert(`Çevrimdışı modda "${actionLabel}" işlemi kapalıdır.`);
      return false;
    },
    [isOnline]
  );

  const loadVehicleAdminData = useCallback(async (projectId) => {
    const targetProjectId = String(projectId || "").trim();
    if (!targetProjectId) {
      setVehicleFleet([]);
      setVehicleManagerRole("project_admin");
      return;
    }

    const data = await fetchAdminProjectVehicles(targetProjectId);
    setVehicleFleet(Array.isArray(data?.vehicles) ? data.vehicles : []);
    setVehicleManagerRole(normalizeVehicleManagerRole(data?.managerRole));
  }, []);

  const loadData = useCallback(async () => {
    if (!isOnline) return;

    if (activeTab === "vehicles") {
      setLoading(true);
      try {
        let nextProjects = null;
        if (user.role === "superadmin") {
          nextProjects = await fetchProjects();
          setProjects(nextProjects);
        }

        const targetProjectId =
          user.role === "superadmin"
            ? resolveVehicleScopeProjectId(nextProjects, vehicleProjectId, selectedProjectId)
            : selectedProjectId;

        if (!targetProjectId) {
          setVehicleFleet([]);
          setVehicleManagerRole("project_admin");
          return;
        }

        await loadVehicleAdminData(targetProjectId);
      } catch (error) {
        alert(getAdminApiErrorMessage(error, "Araç havuzu verileri yüklenemedi."));
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      if (user.role === "superadmin") {
        setProjects(await fetchProjects());
      }
      if (activeTab === "personnel") {
        setPersonnel(await fetchAdminPersonnel(scopedProjectId));
      } else if (activeTab === "logs") {
        setLogs(await fetchAdminLogs({
          projectId: scopedProjectId || undefined,
          startDate: logStartDate || undefined,
          endDate: logEndDate || undefined,
          limit: 150,
        }));
      } else if (activeTab === "audit") {
        setAuditLogs(await fetchAdminAuditLogs({
          projectId: scopedProjectId || undefined,
          startDate: logStartDate || undefined,
          endDate: logEndDate || undefined,
          limit: 200,
        }));
      }
    } catch (error) {
      alert(getAdminApiErrorMessage(error, "Veriler yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    isOnline,
    loadVehicleAdminData,
    logEndDate,
    logStartDate,
    scopedProjectId,
    selectedProjectId,
    user.role,
    vehicleProjectId,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateProject = async () => {
    if (!ensureOnline("proje oluşturma")) return;
    if (!newProjectName.trim()) return alert("Proje adı boş olamaz.");
    setLoading(true);
    try {
      await createProject({ name: newProjectName.trim() });
      setNewProjectName("");
      setShowProjectForm(false);
      await loadData();
      alert("Proje başarıyla oluşturuldu.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Proje oluşturulamadı."));
    } finally {
      setLoading(false);
    }
  };

  const handleAddPersonnel = async () => {
    if (!ensureOnline("personel ekleme")) return;
    if (!newPersonnelName.trim() || !newPersonnelEmail.trim() || !newPersonnelUsername.trim() || !newPersonnelPassword.trim()) {
      return alert("Tüm alanlar zorunludur.");
    }
    const username = newPersonnelUsername.trim().toLowerCase();
    let confirmRoleElevation = false;
    let confirmationCode = "";
    if (scopedProjectId && newPersonnelRole === "project_admin") {
      const typed = window.prompt(`Kritik onay: yönetici ataması için kullanıcı adını yazın (${username})`, "");
      if (typed == null) return;
      if (typed.trim().toLowerCase() !== username) return alert("Kullanıcı adı eşleşmedi.");
      confirmRoleElevation = true;
      confirmationCode = username;
    }
    setLoading(true);
    try {
      await createPersonnel({
        name: newPersonnelName.trim(),
        email: newPersonnelEmail.trim(),
        username,
        password: newPersonnelPassword.trim(),
        role: newPersonnelRole,
        projectId: scopedProjectId,
        confirmRoleElevation,
        confirmationCode,
      });
      setNewPersonnelName("");
      setNewPersonnelEmail("");
      setNewPersonnelUsername("");
      setNewPersonnelPassword("");
      setNewPersonnelRole("user");
      setShowPersonnelForm(false);
      await loadData();
      alert("Personel eklendi.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Personel eklenemedi."));
    } finally {
      setLoading(false);
    }
  };

  const openAssignModal = (person) => {
    const nextSelections = {};
    projects.forEach((project) => {
      const projectId = String(project.id);
      const windowInfo = person.assignmentWindows?.[projectId] || {};
      nextSelections[projectId] = {
        selected: person.projectIds?.map(String).includes(projectId) || false,
        role: person.projectRoles?.[projectId] || "user",
        validFrom: toInputDate(windowInfo.validFrom),
        validUntil: toInputDate(windowInfo.validUntil),
      };
    });
    setAssignUser(person);
    setAssignSelections(nextSelections);
  };

  const saveAssignments = async () => {
    if (!assignUser || !ensureOnline("rol atama")) return;
    const assignments = Object.entries(assignSelections)
      .filter(([, selection]) => selection.selected)
      .map(([projectId, selection]) => ({
        projectId,
        role: selection.role,
        validFrom: selection.validFrom || undefined,
        validUntil: selection.validUntil || undefined,
      }));
    const hasElevation = assignments.some(
      (assignment) => assignment.role === "project_admin" && assignUser.projectRoles?.[assignment.projectId] !== "project_admin"
    );
    const options = {};
    if (hasElevation) {
      const expectedUsername = String(assignUser.username || "").trim().toLowerCase();
      const typed = window.prompt(`Kritik onay: yönetici yükseltmesi için kullanıcı adını yazın (${expectedUsername})`, "");
      if (typed == null) return;
      if (typed.trim().toLowerCase() !== expectedUsername) return alert("Kullanıcı adı eşleşmedi.");
      options.confirmRoleElevation = true;
      options.confirmationCode = expectedUsername;
    }
    setLoading(true);
    try {
      await assignPersonnel(assignUser.id, assignments, options);
      setAssignUser(null);
      await loadData();
      alert("Görevlendirmeler güncellendi.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Görevlendirmeler kaydedilemedi."));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePersonnel = async (person) => {
    if (!ensureOnline("personel silme")) return;
    const confirmed = window.confirm(
      user.role === "superadmin"
        ? `"${person.name}" kullanıcısını sistemden tamamen silmek istediğinize emin misiniz?`
        : `"${person.name}" kullanıcısını bu projeden çıkarmak istediğinize emin misiniz?`
    );
    if (!confirmed) return;
    setLoading(true);
    try {
      await removePersonnel(person.id, scopedProjectId);
      await loadData();
      alert(user.role === "superadmin" ? "Personel sistemden silindi." : "Personel projeden çıkarıldı.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Personel silinemedi."));
    } finally {
      setLoading(false);
    }
  };

  const saveEditPersonnel = async () => {
    if (!editUser || !ensureOnline("personel güncelleme")) return;
    const projectScopedAdmin = Boolean(scopedProjectId && user.role !== "superadmin");
    const current = personnel.find((item) => item.id === editUser.id);
    const wasActive = current?.active !== false;
    const willBeActive = editUser.active !== false;
    const nextPassword = String(editUser.newPassword || "");
    let confirmDeactivation = false;
    let deactivationCode = "";
    let confirmPasswordReset = false;
    let passwordResetCode = "";

    if (projectScopedAdmin) {
      if (!nextPassword.trim()) {
        return alert("Şifre sıfırlama için yeni şifre girmelisiniz.");
      }
      const expectedUsername = String(editUser.username || "").trim().toLowerCase();
      const typed = window.prompt(
        `Kritik onay: şifre sıfırlamak için kullanıcı adını yazın (${expectedUsername})`,
        ""
      );
      if (typed == null) return;
      if (typed.trim().toLowerCase() !== expectedUsername) {
        return alert("Kullanıcı adı eşleşmedi.");
      }

      confirmPasswordReset = true;
      passwordResetCode = expectedUsername;
    } else if (wasActive && !willBeActive) {
      const expectedUsername = String(editUser.username || "").trim().toLowerCase();
      const typed = window.prompt(
        `Kritik onay: kullanıcıyı pasife almak için kullanıcı adını yazın (${expectedUsername})`,
        ""
      );
      if (typed == null) return;
      if (typed.trim().toLowerCase() !== expectedUsername) return alert("Kullanıcı adı eşleşmedi.");
      confirmDeactivation = true;
      deactivationCode = expectedUsername;
    }

    try {
      const payload = {
        projectId: scopedProjectId || undefined,
      };

      if (projectScopedAdmin) {
        payload.newPassword = nextPassword;
        payload.confirmPasswordReset = confirmPasswordReset;
        payload.passwordResetCode = passwordResetCode;
      } else {
        if (!editUser.name?.trim()) return alert("Ad soyad boş olamaz.");
        payload.name = editUser.name.trim();
        payload.email = editUser.email?.trim() || "";
        payload.active = willBeActive;
        payload.newPassword = nextPassword || undefined;
        payload.confirmDeactivation = confirmDeactivation;
        payload.deactivationCode = deactivationCode;
      }

      await updatePersonnel(editUser.id, payload);
      setEditUser(null);
      await loadData();
      alert(projectScopedAdmin ? "Şifre sıfırlandı." : "Personel güncellendi.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Personel güncellenemedi."));
    }
  };

  const handleSelectionChange = (projectId, field, value) => {
    setAssignSelections((previous) => ({
      ...previous,
      [projectId]: { ...previous[projectId], [field]: value },
    }));
  };

  const formatDate = (timestamp) => (timestamp ? new Date(timestamp).toLocaleString("tr-TR") : "-");
  const formatDateOnly = (timestamp) =>
    timestamp
      ? new Intl.DateTimeFormat("tr-TR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(new Date(timestamp))
      : "-";

  const handleAddVehicle = async () => {
    if (!vehicleScopeProjectId) return alert("Önce bir proje seçin.");
    if (!canManageVehicleFleet) {
      return alert("Bu projede araç havuzunu yönetme yetkiniz yok.");
    }

    const normalizedPlate = normalizePlate(newVehiclePlate);
    if (!normalizedPlate) return alert("Plaka boş olamaz.");

    const duplicateVehicle = vehicleFleet.find(
      (vehicle) => normalizePlate(vehicle.plate) === normalizedPlate
    );
    if (duplicateVehicle) {
      return alert("Bu plaka zaten bu proje için kayıtlı.");
    }

    setLoading(true);
    try {
      const createdVehicle = await createAdminProjectVehicle(vehicleScopeProjectId, {
        plate: normalizedPlate,
      });
      setVehicleFleet((previous) => [...previous, createdVehicle]);
      setNewVehiclePlate("");
      alert("Araç kaydedildi.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Araç kaydedilemedi."));
    } finally {
      setLoading(false);
    }
  };

  const handleEditVehicle = (vehicle) => {
    if (!vehicleScopeProjectId) return;
    if (!canManageVehicleFleet) {
      return alert("Bu projede araç havuzunu yönetme yetkiniz yok.");
    }

    setVehicleEditForm({
      id: vehicle.id,
      sourceProjectId: String(vehicle.projectId || vehicleScopeProjectId),
      projectId: String(vehicle.projectId || vehicleScopeProjectId),
      plate: vehicle.plate || "",
      active: vehicle.active !== false,
      initialActive: vehicle.active !== false,
    });
  };

  const handleSaveVehicleEdit = async () => {
    if (!vehicleEditForm) return;
    if (!ensureOnline("araç düzenleme")) return;
    if (!canManageVehicleFleet) {
      return alert("Bu projede araç havuzunu yönetme yetkiniz yok.");
    }

    const normalizedPlate = normalizePlate(vehicleEditForm.plate);
    if (!normalizedPlate) return alert("Plaka boş olamaz.");
    const targetProjectId = String(vehicleEditForm.projectId || "").trim();
    if (!targetProjectId) return alert("Araç projesi boş olamaz.");
    if (!isSuperAdmin && targetProjectId !== vehicleEditForm.sourceProjectId) {
      return alert("Aracı başka projeye sadece Süper Admin taşıyabilir.");
    }

    const duplicateVehicle = vehicleFleet.find(
      (item) =>
        targetProjectId === vehicleEditForm.sourceProjectId &&
        item.id !== vehicleEditForm.id &&
        normalizePlate(item.plate) === normalizedPlate
    );
    if (duplicateVehicle) {
      return alert("Bu plaka zaten bu proje için kayıtlı.");
    }

    if (targetProjectId !== vehicleEditForm.sourceProjectId) {
      const targetProjectName =
        projects.find((project) => String(project.id) === targetProjectId)?.name ||
        targetProjectId;
      const confirmed = window.confirm(
        `Araç ${targetProjectName} projesine taşınacak. Geçmiş km ve yakıt kayıtları da araçla birlikte taşınacak. Devam edilsin mi?`
      );
      if (!confirmed) return;
    }

    setLoading(true);
    try {
      const updatePayload = {
        plate: normalizedPlate,
        projectId: targetProjectId,
      };
      if (vehicleEditForm.active !== vehicleEditForm.initialActive) {
        updatePayload.active = vehicleEditForm.active;
      }

      const updatedVehicle = await updateAdminProjectVehicle(
        vehicleEditForm.sourceProjectId,
        vehicleEditForm.id,
        updatePayload
      );
      setVehicleFleet((previous) =>
        targetProjectId === vehicleEditForm.sourceProjectId
          ? previous.map((item) =>
              item.id === vehicleEditForm.id ? updatedVehicle : item
            )
          : previous.filter((item) => item.id !== vehicleEditForm.id)
      );
      setVehicleEditForm(null);
      alert("Araç bilgisi güncellendi.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Araç bilgisi güncellenemedi."));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVehicleActive = async (vehicle) => {
    if (!vehicleScopeProjectId) return;
    if (!canManageVehicleFleet) {
      return alert("Bu projede araç havuzunu yönetme yetkiniz yok.");
    }

    const nextActive = vehicle.active === false;
    const confirmation = window.confirm(
      nextActive
        ? `"${vehicle.plate}" aracını tekrar aktif yapmak istiyor musunuz?`
        : `"${vehicle.plate}" aracını pasife almak istiyor musunuz? Eski kayıtlar görünmeye devam eder.`
    );
    if (!confirmation) return;

    setLoading(true);
    try {
      const updatedVehicle = await updateAdminProjectVehicle(
        vehicleScopeProjectId,
        vehicle.id,
        { active: nextActive }
      );
      setVehicleFleet((previous) =>
        previous.map((item) =>
          item.id === vehicle.id ? updatedVehicle : item
        )
      );
      alert(nextActive ? "Araç tekrar aktifleştirildi." : "Araç pasife alındı.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Araç durumu güncellenemedi."));
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleManagerRoleSave = async () => {
    if (!vehicleScopeProjectId) return alert("Önce bir proje seçin.");
    if (!canEditVehiclePermission) {
      return alert("Araç havuzu yetkisini sadece baş admin değiştirebilir.");
    }

    setLoading(true);
    try {
      const data = await updateAdminProjectVehicleSettings(vehicleScopeProjectId, {
        managerRole: vehicleManagerRole,
      });
      const savedRole = normalizeVehicleManagerRole(data?.managerRole);
      setVehicleManagerRole(savedRole);
      alert("Araç havuzu yetkisi güncellendi.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Araç havuzu yetkisi güncellenemedi."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1 className="admin-title">Çevre Araştırmaları - Admin Paneli</h1>
          <div className="admin-user-info">
            <button onClick={onBackToMap} className="back-button" style={{ ...buttonStyle, backgroundColor: "#10b981", color: "white", marginRight: 15 }}>Haritaya Dön</button>
            <span className="admin-username">{user.displayName}</span>
            <button onClick={onLogout} className="logout-button">Çıkış Yap</button>
          </div>
        </div>
      </div>
      {!isOnline ? <div className="admin-offline-banner">Çevrimdışı modda admin işlemleri kapalıdır.</div> : null}
      <div className="admin-tabs">
        {user.role === "superadmin" ? <button className={`admin-tab ${activeTab === "projects" ? "active" : ""}`} onClick={() => setActiveTab("projects")}>Projeler</button> : null}
        <button className={`admin-tab ${activeTab === "personnel" ? "active" : ""}`} onClick={() => setActiveTab("personnel")}>Personel Yönetimi</button>
        <button className={`admin-tab ${activeTab === "vehicles" ? "active" : ""}`} onClick={() => setActiveTab("vehicles")}>Araç Havuzu</button>
        <button className={`admin-tab ${activeTab === "logs" ? "active" : ""}`} onClick={() => setActiveTab("logs")}>Sistem Logları</button>
        <button className={`admin-tab ${activeTab === "audit" ? "active" : ""}`} onClick={() => setActiveTab("audit")}>Denetim Kayıtları</button>
        <button className={`admin-tab ${activeTab === "matrix" ? "active" : ""}`} onClick={() => setActiveTab("matrix")}>Yetki Matrisi</button>
      </div>
      <div className="admin-content">
        {loading ? <div className="loading-overlay"><div className="spinner" /><p>Yükleniyor...</p></div> : null}

        {assignUser ? <div style={modalOverlayStyle}><div style={modalCardStyle}><h3 style={{ marginTop: 0 }}>{assignUser.name} - Proje Görevlendirme</h3><div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{projects.map((project) => { const projectId = String(project.id); const s = assignSelections[projectId] || {}; return <div key={project.id} style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}><label style={{ display: "flex", alignItems: "center", gap: 10 }}><input type="checkbox" checked={s.selected || false} onChange={(event) => handleSelectionChange(projectId, "selected", event.target.checked)} />{project.name}</label>{s.selected ? <select value={s.role || "user"} onChange={(event) => handleSelectionChange(projectId, "role", event.target.value)}><option value="user">Kullanıcı</option><option value="project_admin">Yönetici</option></select> : null}</div>{s.selected && s.role === "project_admin" ? <div className="assignment-window-grid"><div><label className="assignment-window-label">Başlangıç</label><input type="date" value={s.validFrom || ""} onChange={(event) => handleSelectionChange(projectId, "validFrom", event.target.value)} /></div><div><label className="assignment-window-label">Bitiş</label><input type="date" value={s.validUntil || ""} onChange={(event) => handleSelectionChange(projectId, "validUntil", event.target.value)} /></div></div> : null}</div>; })}</div><div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}><button onClick={() => setAssignUser(null)} style={{ ...buttonStyle, background: "white", border: "1px solid #cbd5e1" }}>İptal</button><button onClick={saveAssignments} style={{ ...buttonStyle, background: "#10b981", color: "white" }} disabled={!isOnline}>Kaydet</button></div></div></div> : null}

        {activeTab === "projects" ? <div className="admin-section"><div className="section-header"><h2>Proje Yönetimi</h2><button onClick={() => setShowProjectForm((value) => !value)} className="add-button">{showProjectForm ? "İptal" : "Yeni Proje"}</button></div>{showProjectForm ? <div className="form-card"><h3>Yeni Proje Oluştur</h3><div className="form-group"><label>Proje Adı</label><input type="text" value={newProjectName} onChange={(event) => setNewProjectName(event.target.value)} /></div><button onClick={handleCreateProject} className="submit-button" disabled={!isOnline}>Oluştur</button></div> : null}<div className="table-container"><table className="admin-table"><thead><tr><th>Proje ID</th><th>Proje Adı</th><th>Oluşturulma Tarihi</th><th>Oluşturan</th></tr></thead><tbody>{projects.length === 0 ? <tr><td colSpan="4" className="empty-state">Henüz proje yok</td></tr> : projects.map((project) => <tr key={project.id}><td><code>{project.id}</code></td><td>{project.name || "-"}</td><td>{formatDate(project.createdAt)}</td><td>{project.createdBy || "-"}</td></tr>)}</tbody></table></div></div> : null}

        {activeTab === "personnel" ? (
          <div className="admin-section">
            <div className="section-header">
              <h2>Personel Havuzu {user.role !== "superadmin" ? "(Proje İçi)" : ""}</h2>
              <button onClick={() => setShowPersonnelForm((value) => !value)} className="add-button">
                {showPersonnelForm ? "İptal" : "Yeni Personel"}
              </button>
            </div>
            <p className="scope-note">{scopeTitle}</p>
            {showPersonnelForm ? (
              <div className="form-card">
                <h3>Yeni Personel Ekle</h3>
                <div className="form-group">
                  <label>Ad Soyad</label>
                  <input type="text" value={newPersonnelName} onChange={(event) => setNewPersonnelName(event.target.value)} />
                </div>
                <div className="form-group">
                  <label>E-posta</label>
                  <input type="email" value={newPersonnelEmail} onChange={(event) => setNewPersonnelEmail(event.target.value)} />
                </div>
                <div className="form-group">
                  <label>Kullanıcı Adı</label>
                  <input type="text" value={newPersonnelUsername} onChange={(event) => setNewPersonnelUsername(event.target.value)} />
                </div>
                <div className="form-group">
                  <label>Şifre</label>
                  <input type="text" value={newPersonnelPassword} onChange={(event) => setNewPersonnelPassword(event.target.value)} />
                </div>
                {scopedProjectId ? (
                  <div className="form-group">
                    <label>Rol</label>
                    <select value={newPersonnelRole} onChange={(event) => setNewPersonnelRole(event.target.value)}>
                      <option value="user">Kullanıcı</option>
                      <option value="project_admin">Yönetici</option>
                    </select>
                  </div>
                ) : null}
                <button onClick={handleAddPersonnel} className="submit-button" disabled={!isOnline}>
                  Kaydet
                </button>
              </div>
            ) : null}

            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Ad Soyad</th>
                    <th>Kullanıcı Adı</th>
                    <th>E-posta</th>
                    <th>Proje Kapsamı</th>
                    <th>Yetki Seviyesi</th>
                    <th>Görev</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {personnel.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-state">Henüz personel yok</td>
                    </tr>
                  ) : (
                    personnel.map((person) => {
                      const scopedRole = scopedProjectId ? person.projectRoles?.[scopedProjectId] || null : null;
                      const adminInfo = getPersonnelAdminInfo(person, scopedProjectId);
                      return (
                        <tr key={person.id}>
                          <td>{person.name || "-"}</td>
                          <td><code>{person.username || "-"}</code></td>
                          <td>{person.email || "-"}</td>
                          <td>
                            {user.role === "superadmin"
                              ? (person.projectIds?.length ? `${person.projectIds.length} projede görevli` : "Atanmamış")
                              : scopedRole ? `Bu projede ${roleLabel(scopedRole)}` : "Bu projede atanmamış"}
                          </td>
                          <td>
                            <span className={`role-badge ${adminInfo.badgeClass}`}>
                              {adminInfo.adminTitle}
                            </span>
                          </td>
                          <td>{adminInfo.duty}</td>
                          <td>
                            <span className={person.active ? "status-active" : "status-inactive"}>
                              {person.active ? "Aktif" : "Pasif"}
                            </span>
                          </td>
                          <td style={{ display: "flex", gap: 10 }}>
                            {user.role === "superadmin" ? (
                              <button onClick={() => openAssignModal(person)} style={{ ...buttonStyle, background: "#3b82f6", color: "white" }} disabled={!isOnline}>
                                Projeye Ata
                              </button>
                            ) : null}
                            <button onClick={() => setEditUser({ ...person, newPassword: "" })} style={{ ...buttonStyle, background: "#f59e0b", color: "white" }}>
                              {user.role === "superadmin" ? "Düzenle" : "Şifre Sıfırla"}
                            </button>
                            <button onClick={() => handleDeletePersonnel(person)} style={{ ...buttonStyle, background: "#dc2626", color: "white" }} disabled={!isOnline}>
                              Sil
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {editUser ? (
              <div style={modalOverlayStyle}>
                <div style={{ ...modalCardStyle, width: 440 }}>
                  <h3 style={{ marginTop: 0 }}>
                    {user.role === "superadmin" ? "Personel Düzenle" : "Şifre Sıfırla"}
                  </h3>
                  <div className="form-group">
                    <label>Kullanıcı Adı</label>
                    <input type="text" value={editUser.username || ""} disabled />
                  </div>
                  {user.role === "superadmin" ? (
                    <>
                      <div className="form-group">
                        <label>Ad Soyad</label>
                        <input type="text" value={editUser.name || ""} onChange={(event) => setEditUser({ ...editUser, name: event.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>E-posta</label>
                        <input type="email" value={editUser.email || ""} onChange={(event) => setEditUser({ ...editUser, email: event.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>Durum</label>
                        <select value={editUser.active === false ? "inactive" : "active"} onChange={(event) => setEditUser({ ...editUser, active: event.target.value !== "inactive" })}>
                          <option value="active">Aktif</option>
                          <option value="inactive">Pasif</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <p className="scope-note" style={{ marginTop: 0 }}>
                      Proje yöneticisi bu ekranda yalnızca şifre sıfırlayabilir.
                    </p>
                  )}
                  <div className="form-group">
                    <label>Yeni Şifre</label>
                    <input
                      type="text"
                      value={editUser.newPassword || ""}
                      onChange={(event) => setEditUser({ ...editUser, newPassword: event.target.value })}
                      placeholder={
                        user.role === "superadmin"
                          ? "Boş bırakırsanız değişmez"
                          : "Yeni geçici şifre giriniz"
                      }
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                    <button onClick={() => setEditUser(null)} style={{ ...buttonStyle, background: "white", border: "1px solid #cbd5e1" }}>
                      İptal
                    </button>
                    <button onClick={saveEditPersonnel} style={{ ...buttonStyle, background: "#10b981", color: "white" }} disabled={!isOnline}>
                      Kaydet
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {activeTab === "vehicles" ? (
          <div className="admin-section admin-section--vehicles">
            <div className="section-header">
              <h2>Araç Havuzu</h2>
            </div>
            <p className="scope-note">
              Kapsam: {vehicleScopeProjectName} | Pasife alınan araçların eski km ve yakıt kayıtları görünmeye devam eder.
            </p>

            <div className="vehicle-admin-grid">
              <div className="form-card">
                <h3>Araç Yetkisi</h3>
                <div className="form-group">
                  <label>Araç havuzunu kim yönetebilir?</label>
                  <select
                    value={vehicleManagerRole}
                    onChange={(event) => setVehicleManagerRole(event.target.value)}
                    disabled={!canEditVehiclePermission}
                  >
                    <option value="project_admin">Proje Yöneticisi de Yapabilir</option>
                    <option value="superadmin">Sadece Baş Admin</option>
                  </select>
                </div>
                <button
                  onClick={handleVehicleManagerRoleSave}
                  className="submit-button"
                  disabled={!canEditVehiclePermission}
                >
                  Yetkiyi Kaydet
                </button>
                {!canEditVehiclePermission ? (
                  <p className="scope-note" style={{ marginTop: 12 }}>
                    Araç havuzu yetki ayarını sadece baş admin değiştirebilir.
                  </p>
                ) : null}
              </div>

              <div className="form-card">
                <h3>Yeni Araç Ekle</h3>
                {user.role === "superadmin" ? (
                  <div className="form-group">
                    <label>Araç hangi proje için kullanılacak?</label>
                    <select
                      value={vehicleScopeProjectId || ""}
                      onChange={(event) => setVehicleProjectId(event.target.value)}
                      disabled={projects.length === 0}
                    >
                      {projects.length === 0 ? (
                        <option value="">Önce proje oluşturun</option>
                      ) : (
                        projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                ) : (
                  <p className="scope-note" style={{ marginTop: 0 }}>
                    Bu araçlar {vehicleScopeProjectName} projesi kapsamında görünür.
                  </p>
                )}
                <p className="scope-note" style={{ marginTop: 0 }}>
                  Seçilen proje, aracın km ve yakıt ekranlarında kimlere görüneceğini belirler.
                </p>
                <div className="form-group">
                  <label>Plaka</label>
                  <input
                    type="text"
                    value={newVehiclePlate}
                    onChange={(event) => setNewVehiclePlate(event.target.value)}
                    disabled={!canManageVehicleFleet || !vehicleScopeProjectId}
                  />
                </div>
                <button
                  onClick={handleAddVehicle}
                  className="submit-button"
                  disabled={!canManageVehicleFleet || !vehicleScopeProjectId}
                >
                  Aracı Kaydet
                </button>
                {!canManageVehicleFleet ? (
                  <p className="scope-note" style={{ marginTop: 12 }}>
                    Bu projede araç havuzunu yönetme yetkiniz yok.
                  </p>
                ) : !vehicleScopeProjectId ? (
                  <p className="scope-note" style={{ marginTop: 12 }}>
                    Araç eklemek için önce bir proje seçin.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Plaka</th>
                    <th>Durum</th>
                    <th>Eklenme</th>
                    <th>Pasife Alınma</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicleFleet.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-state">Henüz araç yok</td>
                    </tr>
                  ) : (
                    vehicleFleet.map((vehicle) => (
                      <tr key={vehicle.id}>
                        <td>{vehicle.plate}</td>
                        <td>
                          <span className={vehicle.active === false ? "status-inactive" : "status-active"}>
                            {vehicle.active === false ? "Pasif" : "Aktif"}
                          </span>
                        </td>
                        <td>{formatDateOnly(vehicle.createdAt)}</td>
                        <td>{formatDateOnly(vehicle.retiredAt)}</td>
                        <td style={{ display: "flex", gap: 10 }}>
                          <button
                            onClick={() => handleEditVehicle(vehicle)}
                            style={{ ...buttonStyle, background: "#f59e0b", color: "white" }}
                            disabled={!canManageVehicleFleet}
                          >
                            Düzenle
                          </button>
                          <button
                            onClick={() => handleToggleVehicleActive(vehicle)}
                            style={{
                              ...buttonStyle,
                              background: vehicle.active === false ? "#2563eb" : "#dc2626",
                              color: "white",
                            }}
                            disabled={!canManageVehicleFleet}
                          >
                            {vehicle.active === false ? "Aktifleştir" : "Pasife Al"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {vehicleEditForm ? (
              <div style={modalOverlayStyle}>
                <div style={{ ...modalCardStyle, width: 440 }}>
                  <h3 style={{ marginTop: 0 }}>Araç Düzenle</h3>
                  <div className="form-group">
                    <label>Plaka</label>
                    <input
                      type="text"
                      value={vehicleEditForm.plate}
                      onChange={(event) =>
                        setVehicleEditForm((previous) => ({
                          ...previous,
                          plate: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Proje</label>
                    {isSuperAdmin ? (
                      <select
                        value={vehicleEditForm.projectId}
                        onChange={(event) =>
                          setVehicleEditForm((previous) => ({
                            ...previous,
                            projectId: event.target.value,
                          }))
                        }
                      >
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input type="text" value={vehicleScopeProjectName} disabled />
                    )}
                  </div>
                  <div className="form-group">
                    <label>Durum</label>
                    <select
                      value={vehicleEditForm.active ? "active" : "passive"}
                      onChange={(event) =>
                        setVehicleEditForm((previous) => ({
                          ...previous,
                          active: event.target.value === "active",
                        }))
                      }
                    >
                      <option value="active">Aktif</option>
                      <option value="passive">Pasif</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                    <button
                      onClick={() => setVehicleEditForm(null)}
                      style={{ ...buttonStyle, background: "white", border: "1px solid #cbd5e1" }}
                      disabled={loading}
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleSaveVehicleEdit}
                      style={{ ...buttonStyle, background: "#10b981", color: "white" }}
                      disabled={loading || !isOnline}
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {activeTab === "logs" ? <div className="admin-section"><div className="section-header"><h2>Sistem Logları</h2><div style={{ display: "flex", alignItems: "center", gap: 10 }}><input type="date" value={logStartDate} onChange={(event) => setLogStartDate(event.target.value)} /><input type="date" value={logEndDate} onChange={(event) => setLogEndDate(event.target.value)} /><button onClick={loadData} className="refresh-button" disabled={!isOnline}>Ara / Yenile</button></div></div><p className="scope-note">{scopeTitle}</p><div className="table-container"><table className="admin-table"><thead><tr><th>Tarih/Saat</th><th>Tip</th><th>Mesaj</th><th>Kullanıcı</th></tr></thead><tbody>{logs.length === 0 ? <tr><td colSpan="4" className="empty-state">Henüz log kaydı yok</td></tr> : logs.map((log) => <tr key={log.id}><td>{formatDate(log.timestamp)}</td><td><span className={`log-type ${log.type}`}>{log.type}</span></td><td>{log.message || "-"}</td><td>{log.userId || "-"}</td></tr>)}</tbody></table></div></div> : null}

        {activeTab === "audit" ? <div className="admin-section"><div className="section-header"><h2>Denetim Kayıtları</h2><div style={{ display: "flex", alignItems: "center", gap: 10 }}><input type="date" value={logStartDate} onChange={(event) => setLogStartDate(event.target.value)} /><input type="date" value={logEndDate} onChange={(event) => setLogEndDate(event.target.value)} /><button onClick={loadData} className="refresh-button" disabled={!isOnline}>Ara / Yenile</button></div></div><p className="scope-note">{scopeTitle}</p><div className="table-container"><table className="admin-table"><thead><tr><th>Tarih/Saat</th><th>Olay</th><th>Mesaj</th><th>İşlemi Yapan</th><th>Hedef</th><th>Proje</th></tr></thead><tbody>{auditLogs.length === 0 ? <tr><td colSpan="6" className="empty-state">Henüz denetim kaydı yok</td></tr> : auditLogs.map((log) => <tr key={log.id}><td>{formatDate(log.timestamp)}</td><td><span className="log-type">{log.eventType}</span></td><td>{log.eventMessage || "-"}</td><td>{log.actorUsername || "-"}</td><td>{log.targetUsername || "-"}</td><td>{log.projectName || "-"}</td></tr>)}</tbody></table></div></div> : null}

        {activeTab === "matrix" ? <div className="admin-section"><div className="section-header"><h2>Yetki Matrisi</h2></div><p className="scope-note">UI ve API aynı yetki kurallarını uygular.</p><div className="table-container"><table className="admin-table matrix-table"><thead><tr><th>İşlem</th><th>Baş Admin</th><th>Proje Admin</th><th>Kullanıcı</th></tr></thead><tbody>{ROLE_MATRIX_ROWS.map((row) => <tr key={row[0]}><td>{row[0]}</td><td>{row[1]}</td><td>{row[2]}</td><td>{row[3]}</td></tr>)}</tbody></table></div></div> : null}
      </div>
    </div>
  );
}
